import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// CSS IMPORTS
import './assets/scss/style.scss';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';

// COMPONENT IMPORTS
import { getFullDomain } from './utils/common';
import { decodeToken } from './utils/jwtUtils';
import { initializeSocket } from './config/Socket';
import AuthLayout from './components/layout/AuthLayout';
import MainLayout from './components/layout/MainLayout';
import AdminLayout from './components/layout/AdminLayout';
import defaultFavicon from './assets/images/logos/callex.png';
import MasterAdminLayout from './components/layout/MasterAdminLayout';

// PAGES IMPORTS
import Crm from './pages/CRM/Crm';
import Chat from './pages/Chat/Chat';
import Dashboard from './pages/Dashboard';
import Auth from './pages/authentication/Auth';
import ChatBot from './pages/Chat Bot/ChatBot';
import Helpdesk from './pages/Helpdesk/Helpdesk';
import FollowUp from './pages/FollowUp/FollowUp';
import CrmScript from './pages/Script/CrmScript';
import Ivr from './pages/TenantAdmin/Incoming/Ivr';
import DynamicIvr from './pages/TenantAdmin/Incoming/DynamicIvr';
import Dnc from './pages/TenantAdmin/Incoming/Dnc';
import CrmProduct from './pages/Product/CrmProduct';
import Dids from './pages/TenantAdmin/Incoming/Dids';
import InboundAcl from './pages/TenantAdmin/Incoming/InboundAcl';
import PageNotFound from './components/PageNotFound';
import SessionEnded from './components/SessionEnded';
import CallInProgress from './components/CallInProgress';
import Trunk from './pages/TenantAdmin/Routing/Trunk';
import Breaks from './pages/TenantAdmin/Setting/Breaks';
import Beat from './pages/TenantAdmin/Product/Beat/Beat';
import Area from './pages/TenantAdmin/Product/Area/Area';
import Queues from './pages/TenantAdmin/Incoming/Queues';
import Scripts from './pages/TenantAdmin/Setting/Scripts';
import Crms from './pages/TenantAdmin/Configuration/Crms';
import CallHistory from './pages/CallHistory/CallHistory';
import CallSummary from './pages/CallSummary/CallSummary';
import Smtp from './pages/TenantAdmin/Configuration/Smtp';
import LeadPreview from './pages/LeadPreview/LeadPreview';
import Auditor from './pages/TenantAdmin/Auditor/Auditor';
import Users from './pages/TenantAdmin/Configuration/Users';
import Lists from './pages/TenantAdmin/Configuration/Lists';
import Outbound from './pages/TenantAdmin/Routing/Outbound';
import Branding from './pages/MasterAdmin/Branding/Branding';
import MetaHelpdesk from './pages/MetaHelpdesk/MetaHelpdesk';
import Timezone from './pages/MasterAdmin/Timezone/Timezone';
import MasterLogin from './pages/authentication/MasterLogin'; // Import MasterLogin
import HotKey from './pages/TenantAdmin/Configuration/HotKey';
import AuditorLayout from './components/layout/AuditorLayout';
import AuditorLogin from './pages/authentication/AuditorLogin';
import Product from './pages/TenantAdmin/Product/List/Product';
import Process from './pages/TenantAdmin/Configuration/Process';
import Companies from './pages/MasterAdmin/Companies/Companies';
import Disposition from './pages/TenantAdmin/Setting/Disposition';
import LiveCallHistory from './pages/CallHistory/LiveCallHistory';
import LiveThreeSixtyDetailsReport from './pages/CallHistory/LiveThreeSixtyDetailsReport';
import AuditReport from './pages/Auditor/AuditReport/AuditReport';
import Whatsapp from './pages/TenantAdmin/Configuration/Whatsapp';
import AiAgents from './pages/TenantAdmin/Configuration/AiAgents';
import DumpReport from './pages/TenantAdmin/DumpReport/DumpReport';
import Supervisor from './pages/TenantAdmin/Supervisor/Supervisor';
import ChatReport from './pages/TenantAdmin/Call Report/ChatReport';
import AudioPrompts from './pages/TenantAdmin/Incoming/AudioPrompts';
import CompanyRole from './pages/MasterAdmin/CompanyRole/CompanyRole';
import ManualAuditor from './pages/TenantAdmin/Auditor/ManualAuditor';
import ListUpload from './pages/TenantAdmin/Configuration/ListUpload';
import DailyReport from './pages/TenantAdmin/DailyReport/DailyReport';
import SubDisposition from './pages/TenantAdmin/Setting/SubDisposition';
import RealtimeReport from './pages/TenantAdmin/Reports/RealtimeReport';
import AiRealtimeReport from './pages/TenantAdmin/Reports/AiRealtimeReport';
import LeadRetries from './pages/TenantAdmin/Configuration/LeadRetries';
import MyMissedCallReport from './pages/CallHistory/MyMissedCallReport';
import DidDumpReport from './pages/TenantAdmin/DumpReport/DidDumpReport';
import BroadcastReport from './pages/TenantAdmin/Reports/BroadcastReport';
import AuditorCategory from './pages/TenantAdmin/Auditor/AuditorCategory';
import WhatsappMeta from './pages/TenantAdmin/Configuration/WhatsappMeta';
import SupervisorRole from './pages/TenantAdmin/Supervisor/SupervisorRole';
import ProductReport from './pages/TenantAdmin/ProductReport/ProductReport';
import DidDailyReport from './pages/TenantAdmin/DailyReport/DidDailyReport';
import AiPhoneNumber from './pages/TenantAdmin/Configuration/AiPhoneNumber';
import DtmfReport from './pages/TenantAdmin/VoiceBroadcastReport/DtmfReport';
import SubSubDisposition from './pages/TenantAdmin/Setting/SubSubDisposition';
import RecordingReport from './pages/TenantAdmin/Call Report/RecordingReport';
import BothLegsReport from './pages/TenantAdmin/Call Report/BothLegsReport';
import WhatsappReport from './pages/TenantAdmin/Configuration/WhatsappReport';
import FollowUpReport from './pages/TenantAdmin/Configuration/FollowUpReport';
import AdminDashboard from './pages/MasterAdmin/AdminDashboard/AdminDashboard';
import ServerHealth from './pages/MasterAdmin/ServerHealth/ServerHealth';
import MissedCallReport from './pages/TenantAdmin/Call Report/MissedCallReport';
import ThreeSixtyDetailsReport from './pages/TenantAdmin/Call Report/ThreeSixtyDetailsReport';
import DialerCallConnectivityReport from './pages/TenantAdmin/Call Report/DialerCallConnectivityReport';
import BroadcastUpload from './pages/TenantAdmin/Configuration/BroadcastUpload';
import WhatsappBotFlow from './pages/TenantAdmin/Configuration/WhatsappBotFlow';
import AddEditAuditReport from './pages/Auditor/AuditReport/AddEditAuditReport';
import AuditorDashboard from './pages/Auditor/AuditorDashboard/AuditorDashboard';
import TenantAuditReport from './pages/TenantAdmin/Call Report/TenantAuditReport';
import TenantDashboard from './pages/TenantAdmin/TenantDashboard/TenantDashboard';
import WhatsappMetaFlow from './pages/TenantAdmin/Configuration/WhatsappMetaFlow';
import WhatsappBroadcast from './pages/TenantAdmin/Configuration/WhatsappBroadcast';
import WhatsappTemplates from './pages/TenantAdmin/Configuration/WhatsappTemplates';
import WhatsappRedirectMessage from './pages/TenantAdmin/Configuration/WhatsappRedirectMessage';
import ProductOutlet from './pages/TenantAdmin/Product/Product-Outlet/ProductOutlet';
import ModulePermission from './pages/MasterAdmin/ModulePermission/ModulePermission';
import BotBroadcastUpload from './pages/TenantAdmin/Configuration/BotBroadcastUpload';
import DailyAgentReport from './pages/TenantAdmin/Agent Performance/DailyAgentReport';
import CustomAgentReport from './pages/TenantAdmin/Agent Performance/CustomAgentReport';
import MetaBroadcastUpload from './pages/TenantAdmin/Configuration/MetaBroadcastUpload';
import WhatsappBotBroadcast from './pages/TenantAdmin/Configuration/WhatsappBotBroadcast';
import WhatsappBotTemplates from './pages/TenantAdmin/Configuration/WhatsappBotTemplates';
import WhatsappMetaTemplates from './pages/TenantAdmin/Configuration/WhatsappMetaTemplates';
import AgentDispositionReport from './pages/TenantAdmin/Call Report/AgentDispositionReport';
import WhatsappMetaBroadcast from './pages/TenantAdmin/Configuration/WhatsappMetaBroadcast';
import AgentPerformanceReport from './pages/TenantAdmin/Call Report/AgentPerformanceReport';
// import ProcessWiseRecording from './pages/TenantAdmin/Agent Performance/ProcessWiseRecording';
import CallConnectivityReport from './pages/TenantAdmin/VoiceBroadcastReport/CallConnectivityReport';
import AiKey from './pages/TenantAdmin/Configuration/AiKey';
import BotVoices from './pages/TenantAdmin/Configuration/BotVoices';
import BotRag from './pages/TenantAdmin/Configuration/BotRag';
import BotSipRouting from './pages/TenantAdmin/Configuration/BotSipRouting';
import AIDisposition from './pages/TenantAdmin/Setting/AIDisposition';
import AllowIp from './pages/TenantAdmin/Setting/AllowIp';
import PbxDtmf from './pages/TenantAdmin/Setting/PbxDtmf';
import ApiKeys from './pages/TenantAdmin/Setting/ApiKeys';
import LeadRetriesReport from './pages/TenantAdmin/Reports/LeadRetriesReport';

// APIS IMPORTS
import {
  brandingDetailsGet,
  useBrandingDetails
} from './store/slices/adminSlices/brandingDetailSlice';
import { setAuthToken, useToken } from './store/slices/authSlice';
import {
  setCallingModalOpen,
  setLoginUserType,
  setTimezone,
  useLoginUserType
} from './store/slices/commonSlice';
import { pauseModeUpdate, setPauseMode } from './store/slices/callingSlice';
import AddEditAIAgent from './pages/TenantAdmin/Configuration/AddEditAIAgent';
import AiCallLogReport from './pages/TenantAdmin/AI Report/AiCallLogReport';
import CallexAiCallLogReport from './pages/TenantAdmin/AI Report/CallexAiCallLogReport';
import CreditReport from './pages/TenantAdmin/AI Report/CreditReport';
import AiRecordingReport from './pages/TenantAdmin/AI Report/AiRecordingReport';
import AiCdrReport from './pages/TenantAdmin/AI Report/AiCdrReport';
import AiChatReport from './pages/TenantAdmin/AI Report/AiChatReport';
import CallexAiAgents from './pages/TenantAdmin/Configuration/CallexAiAgents';
import AddEditCallexAiAgent from './pages/TenantAdmin/Configuration/AddEditCallexAiAgent';

const App = () => {
  const isEmbedded = window.self !== window.top;

  const dispatch = useDispatch();
  const token = useToken();
  const location = useLocation();
  const navigate = useNavigate();

  const loginUserType = useLoginUserType();
  const brandingDetails = useBrandingDetails();

  const fetchBrandingDetails = async () => {
    try {
      await dispatch(
        brandingDetailsGet({
          company_domain:
            process.env.REACT_APP_MODE === 'LOCAL'
              ? process.env.REACT_APP_LOCAL_DOMAIN
              : getFullDomain()
        })
      ).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchBrandingDetails();
  }, []);

  useEffect(() => {
    const defaultTitle = process.env.REACT_APP_SITE_TITLE;

    if (brandingDetails) {
      document.title = brandingDetails.header_text || defaultTitle;
      const faviconUrl = brandingDetails.favicon_path || defaultFavicon;
      let link = document.querySelector("link[rel*='icon']");

      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }

      link.href = faviconUrl;
    } else {
      document.title = defaultTitle;
      const link = document.querySelector("link[rel*='icon']");
      if (link) link.href = defaultFavicon;
    }
  }, [brandingDetails]);

  const onPauseModeChange = async (user) => {
    try {
      const payload = {
        user_id: user?.id,
        pause_mode:
          user?.process_details?.type === 'OutboundAndPredictive' ||
          user?.process_details?.type === 'BlendedAndPredictive'
            ? 1
            : 0
      };
      const response = await dispatch(pauseModeUpdate(payload)).unwrap();
      if (response && response?.success) {
        dispatch(setPauseMode(payload?.pause_mode));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  (function fetchToken() {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');

    if (tokenFromUrl) {
      localStorage.setItem('callex-token', JSON.stringify(tokenFromUrl));
      dispatch(setAuthToken(tokenFromUrl));
      const user = decodeToken(tokenFromUrl);
      dispatch(setTimezone(user?.time_zone_details?.zone_name));
      dispatch(
        setLoginUserType(
          user?.type === 'TenantAdmin' || user?.type === 'supervisor' ? 'tenant' : 'agent'
        )
      );
      initializeSocket();
      dispatch(setCallingModalOpen(user?.manualcall));
      if (process.env.REACT_APP_AGENT_PAUSE_MODE === 'true') {
        onPauseModeChange(user);
      }
      params.delete('token');
      const cleanUrl =
        window.location.pathname + (params.toString() ? `?${params.toString()}` : '');

      window.history.replaceState({}, document.title, cleanUrl);
    }
  })();

  useEffect(() => {
    if (!isEmbedded) return;

    const currentPath = location.pathname;

    const isAllowed = [
      '/',
      '/dashboard',
      '/crm',
      '/call-in-progress',
      '/session-ended',
      '/my-reports',
      '/my-reports/my-callhistory',
      '/my-reports/my-missed-call-report',
      '/live-callhistory'
    ].includes(currentPath);

    if (!isAllowed) {
      navigate('/page-not-found', { replace: true });
    }
  }, [isEmbedded, location.pathname]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          token ? (
            <Navigate
              to={
                loginUserType === 'tenant'
                  ? '/tenant-dashboard'
                  : loginUserType === 'MasterAdmin'
                    ? '/admin-dashboard'
                    : loginUserType === 'auditor'
                      ? '/auditor-dashboard'
                      : '/dashboard'
              }
              replace
            />
          ) : isEmbedded ? (
            <PageNotFound />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* MainLayout for agent */}
      <Route
        element={token && loginUserType === 'agent' ? <MainLayout /> : <Navigate to="/login" />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/chat-bot" element={<ChatBot />} />
        <Route path="/meta-help-desk" element={<MetaHelpdesk />} />
        <Route path="/help-desk" element={<Helpdesk />} />
        <Route path="/call-summary" element={<CallSummary />} />
        <Route path="/follow-up" element={<FollowUp />} />
        <Route path="/my-reports/my-callhistory" element={<CallHistory />} />
        <Route path="/my-reports/my-missed-call-report" element={<MyMissedCallReport />} />
        <Route path="/live-callhistory" element={<LiveCallHistory />} />
        <Route path="/live-360-details-report" element={<LiveThreeSixtyDetailsReport />} />
        <Route path="/lead-preview" element={<LeadPreview />} />
        <Route path="/crm" element={<Crm />} />
        <Route path="/call-in-progress" element={<CallInProgress />} />
        <Route path="/product" element={<CrmProduct />} />
        <Route path="/script" element={<CrmScript />} />
        <Route path="/page-not-found" element={<PageNotFound />} />
      </Route>

      {/* AdminLayout for tenant */}
      <Route
        element={token && loginUserType === 'tenant' ? <AdminLayout /> : <Navigate to="/login" />}>
        <Route path="/tenant-dashboard" element={<TenantDashboard />} />
        <Route path="/server-health" element={<ServerHealth />} />
        <Route path="/reports/realtime-report" element={<RealtimeReport />} />
        <Route path="/ai-callex">
          <Route path="reports">
            <Route path="ai-realtime-report" element={<AiRealtimeReport />} />
            <Route path="ai-call-logs" element={<AiCallLogReport />} />
            <Route path="callex-ai-call-logs" element={<CallexAiCallLogReport />} />
            <Route path="credit-report" element={<CreditReport />} />
            <Route path="ai-recording" element={<AiRecordingReport />} />
            <Route path="ai-cdrs" element={<AiCdrReport />} />
            <Route path="ai-chat-report" element={<AiChatReport />} />
          </Route>
          <Route path="ai">
            <Route path="ai-agents" element={<AiAgents />} />
            <Route path="ai-agents/new" element={<AddEditAIAgent mode="add" />} />
            <Route path="ai-agents/:agentId/edit" element={<AddEditAIAgent mode="edit" />} />

            <Route path="callex-ai-agents" element={<CallexAiAgents />} />
            <Route path="callex-ai-agents/new" element={<AddEditCallexAiAgent />} />
            <Route path="callex-ai-agents/:agentId/edit" element={<AddEditCallexAiAgent />} />

            <Route path="ai-phone-number" element={<AiPhoneNumber />} />
            <Route path="ai-key" element={<AiKey />} />
            <Route path="bot-voices" element={<BotVoices />} />
            <Route path="bot-rag" element={<BotRag />} />
            <Route path="bot-sip-routing" element={<BotSipRouting />} />
          </Route>
          <Route path="settings">
            <Route path="ai-disposition" element={<AIDisposition />} />
            <Route path="allow-ip" element={<AllowIp />} />
          </Route>
        </Route>
        <Route path="/reports/broadcast-report" element={<BroadcastReport />} />
        <Route path="/reports/daily-report" element={<DailyReport />} />
        <Route path="/reports/dump-report" element={<DumpReport />} />
        <Route path="/reports/did-daily-report" element={<DidDailyReport />} />
        <Route path="/reports/did-dump-report" element={<DidDumpReport />} />
        <Route path="/reports/daily-agent-login-logout" element={<DailyAgentReport />} />
        <Route path="/reports/custom-agent-login-logout" element={<CustomAgentReport />} />
        {/* <Route path="/reports/process-wise-recording" element={<ProcessWiseRecording />} /> */}
        <Route path="/reports/product-report" element={<ProductReport />} />
        <Route path="/reports/dtmf-report" element={<DtmfReport />} />
        <Route path="/reports/call-connectivity-report" element={<CallConnectivityReport />} />
        <Route path="/reports/missed-call-report" element={<MissedCallReport />} />
        <Route path="/reports/360-details-report" element={<ThreeSixtyDetailsReport />} />
        <Route
          path="/reports/dialer-call-connectivity-report"
          element={<DialerCallConnectivityReport />}
        />
        <Route path="/reports/agent-performance-report" element={<AgentPerformanceReport />} />
        <Route path="/reports/agent-disposition-report" element={<AgentDispositionReport />} />
        <Route path="/reports/tenant-audit-report" element={<TenantAuditReport />} />
        <Route path="/reports/recording-report" element={<RecordingReport />} />
        <Route path="/reports/both-legs-report" element={<BothLegsReport />} />
        <Route path="/reports/chat-report" element={<ChatReport />} />
        <Route path="/reports/whatsapp-report" element={<WhatsappReport />} />
        <Route path="/reports/lead-retries-report" element={<LeadRetriesReport />} />
        <Route path="/reports/auditor-dashboard" element={<AuditorDashboard />} />
        <Route path="/reports/auditor-report" element={<AuditReport />} />
        <Route
          path="/reports/auditor-report/:call_history_id/:archiveId"
          element={<AddEditAuditReport />}
        />
        <Route path="/configuration/crms" element={<Crms />} />
        <Route path="/configuration/users" element={<Users />} />
        <Route path="/configuration/process" element={<Process />} />
        <Route path="/configuration/lists" element={<Lists />} />
        <Route path="/configuration/lists/:id" element={<ListUpload />} />
        <Route path="/configuration/lead-retries" element={<LeadRetries />} />
        <Route path="/configuration/omni-channel/whatsapp" element={<Whatsapp />} />
        <Route path="/configuration/setting/disposition" element={<Disposition />} />
        <Route path="/configuration/setting/sub-disposition" element={<SubDisposition />} />
        <Route path="/configuration/setting/sub-sub-disposition" element={<SubSubDisposition />} />
        <Route path="/configuration/setting/pbx-dtmf" element={<PbxDtmf />} />
        <Route path="/configuration/setting/api-keys" element={<ApiKeys />} />
        <Route path="/configuration/setting/breaks" element={<Breaks />} />
        <Route path="/configuration/setting/scripts" element={<Scripts />} />
        <Route path="/configuration/routing/trunks" element={<Trunk />} />
        <Route path="/configuration/routing/outbound" element={<Outbound />} />
        <Route path="/configuration/follow-up" element={<FollowUpReport />} />
        <Route
          path="/configuration/omni-channel/whatsapp-templates"
          element={<WhatsappTemplates />}
        />
        <Route
          path="/configuration/omni-channel/whatsapp-redirect-message"
          element={<WhatsappRedirectMessage />}
        />
        <Route
          path="/configuration/omni-channel/whatsapp-broadcast"
          element={<WhatsappBroadcast />}
        />
        <Route
          path="/configuration/omni-channel/whatsapp-broadcast/:id"
          element={<BroadcastUpload />}
        />
        <Route
          path="/configuration/whatsapp-meta/broadcast/:id"
          element={<MetaBroadcastUpload />}
        />
        <Route path="/configuration/smtp" element={<Smtp />} />
        <Route path="/configuration/hot-key" element={<HotKey />} />
        <Route path="/configuration/whatsapp-meta" element={<WhatsappMeta />} />
        <Route path="/configuration/whatsapp-meta/broadcast" element={<WhatsappMetaBroadcast />} />
        <Route path="/configuration/whatsapp-meta/flow" element={<WhatsappMetaFlow />} />
        <Route path="/configuration/whatsapp-meta/template" element={<WhatsappMetaTemplates />} />
        <Route path="/configuration/whatsapp-bot/broadcast/:id" element={<BotBroadcastUpload />} />
        <Route path="/configuration/whatsapp-bot/broadcast" element={<WhatsappBotBroadcast />} />
        <Route path="/configuration/whatsapp-bot/flow" element={<WhatsappBotFlow />} />
        <Route path="/configuration/whatsapp-bot/template" element={<WhatsappBotTemplates />} />
        <Route path="/configuration/product/area" element={<Area />} />
        <Route path="/configuration/product/beat" element={<Beat />} />
        <Route path="/configuration/product/outlet" element={<ProductOutlet />} />
        <Route path="/configuration/product/lists" element={<Product />} />
        <Route path="/configuration/dnc" element={<Dnc />} />
        <Route path="/incoming/did" element={<Dids />} />
        <Route path="/incoming/queues" element={<Queues />} />
        <Route path="/incoming/ivr" element={<Ivr />} />
        <Route path="/incoming/dynamic-ivr" element={<DynamicIvr />} />
        <Route path="/incoming/inbound-acl" element={<InboundAcl />} />
        <Route path="/incoming/prompts/audio-prompts" element={<AudioPrompts />} />
        <Route path="/monitoring/supervisor" element={<Supervisor />} />
        <Route path="/monitoring/supervisor-role" element={<SupervisorRole />} />
        <Route path="/monitoring/auditor" element={<Auditor />} />
        <Route path="/monitoring/manual-auditor" element={<ManualAuditor />} />
        <Route path="/monitoring/auditor-category" element={<AuditorCategory />} />
      </Route>

      <Route
        element={
          token && loginUserType === 'MasterAdmin' ? (
            <MasterAdminLayout />
          ) : (
            <Navigate to="/master_login" />
          )
        }>
        <Route path="/admin-dashboard" element={<AdminDashboard />} />
        <Route path="/server-health" element={<ServerHealth />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/module-permission" element={<ModulePermission />} />
        <Route path="/company-role" element={<CompanyRole />} />
        <Route path="/timezone" element={<Timezone />} />
        <Route path="/branding" element={<Branding />} />
      </Route>
      <Route
        element={
          token && loginUserType === 'auditor' ? (
            <AuditorLayout />
          ) : (
            <Navigate to="/auditor_login" />
          )
        }>
        <Route path="/auditor-dashboard" element={<AuditorDashboard />} />
        <Route path="/reports/audit-report" element={<AuditReport />} />
        <Route
          path="/reports/audit-report/:call_history_id/:archiveId"
          element={<AddEditAuditReport />}
        />
      </Route>

      {/* Authentication Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Auth />} />
        <Route path="/master_login" element={<MasterLogin />} />
        <Route path="/auditor_login" element={<AuditorLogin />} />
      </Route>

      {/* Public info page shown when the session is ended (e.g. forced logout in an iframe) */}
      <Route path="/session-ended" element={<SessionEnded />} />

      {/* Catch-all route for 404 */}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default App;
